import logo from "../../assets/svgs/logo.svg";
import logoicon from "../../assets/svgs/logoicon.svg";
import { Logout } from "../../assets/svgs/Logout";
import { Layout, Menu, theme } from "antd";
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { Icon } from "../../assets/svgs/Icon";
import { Product } from "../../assets/svgs/Product";
import { Shop } from "../../assets/svgs/Shop";
import { Bag } from "../../assets/svgs/Bag";
import "./DashBoard.scss";
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { logout } from "../../utils/logout";
import { CatalogUpload } from "../../assets/svgs/CatalogUpload";
import { Kyc } from "../../assets/svgs/Kyc";
import { LeftArrow } from "../../assets/svgs/LeftArrow";
import { RightButtonArrow } from "../../assets/svgs/RightButtonArrow";
import { Ondc } from "../../assets/svgs/Ondc";
import { getSession } from "../../utils/session";
import {
  isOpsAdmin,
  isSupportAdmin,
  isFinance,
  isCatalogManager,
  isManagementRole,
  isSuperAdmin,
  isShopAdmin,
  isShopSupervisor,
  isShopPicker,
  isShopStockManager,
  canViewShopSubscription,
  canManageSubscriptions,
} from "../../utils/roles";
import { Employees } from "../../assets/svgs/Employees";
import { Bills } from "../../assets/svgs/Bills";
import { Customers } from "../../assets/svgs/Customers";
import { Offers } from "../../assets/svgs/Offers";
import { Subscription } from "../../assets/svgs/Subscription";
import { SubscriptionPlans } from "../../assets/svgs/SubscriptionPlans";
import { CatUpIcon } from "../../assets/svgs/CatUpIcon";
import { useLocation } from "react-router-dom";
import { useVersionCheckContext } from "../../contexts/VersionCheckContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  selectCurrentShopConfig,
  selectShopId,
} from "../../store/allOrders.selectors";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { updateShopDetails } from "../../store/allShops.slice";

// Phones and small devices: the sidebar becomes a slide-in drawer instead of
// squeezing the fixed-width rail into an unusable strip.
const MOBILE_BREAKPOINT = "(max-width: 767px)";

const { Sider } = Layout;

interface SiderComponentProps {}

type MenuItemDefinition = {
  key: string;
  path: string;
  dynamicPath?: boolean;
  icon: ReactNode;
  getLabel: (role: string) => string;
  isVisible: (role: string, shopConfig?: ShopConfig | null) => boolean;
  order: number;
};

// Shop config type for invoice sync visibility
interface ShopConfig {
  pos?: {
    invoiceSync?: {
      invoiceLinkWithOrder?: string;
    };
  };
}

const MENU_ITEMS: MenuItemDefinition[] = [
  {
    key: "1",
    path: "/dashboard",
    icon: <Icon />,
    getLabel: () => "Dashboard",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isManagementRole(role) ||
      isShopAdmin(role) ||
      isShopSupervisor(role),
    order: 7,
  },
  {
    key: "2",
    path: "/global-catalog-management",
    icon: <Product />,
    getLabel: () => "Global Catalog",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isOpsAdmin(role) ||
      isSupportAdmin(role) ||
      isCatalogManager(role),
    order: 1,
  },
  {
    key: "3",
    path: "/catalog-automation-tool",
    icon: <CatalogUpload />,
    getLabel: () => "Catalog Automation Tool",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isOpsAdmin(role) ||
      isSupportAdmin(role) ||
      isCatalogManager(role),
    order: 2,
  },
  {
    key: "12",
    path: "/cat-up",
    icon: <CatUpIcon />,
    getLabel: () => "CatUp",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isOpsAdmin(role) ||
      isSupportAdmin(role) ||
      isCatalogManager(role),
    order: 3,
  },
  {
    key: "4",
    path: "/all-shops",
    icon: <Shop />,
    getLabel: (role) =>
      isShopAdmin(role) || isShopSupervisor(role) ? "My Shop" : "Shops",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isManagementRole(role) ||
      isShopAdmin(role) ||
      isShopSupervisor(role),
    order: 11,
  },
  {
    key: "5",
    path: "/orders",
    icon: <Bag />,
    getLabel: () => "Orders",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isOpsAdmin(role) ||
      isSupportAdmin(role) ||
      isFinance(role) ||
      isShopAdmin(role) ||
      isShopSupervisor(role) ||
      isShopPicker(role),
    order: 4,
  },
  {
    key: "6",
    path: "/shop-kyc",
    icon: <Kyc />,
    getLabel: () => "Shop KYC",
    isVisible: (role) =>
      isSuperAdmin(role) ||
      isOpsAdmin(role) ||
      isSupportAdmin(role) ||
      isFinance(role),
    order: 12,
  },
  {
    key: "9",
    path: "/ondc-report",
    icon: <Ondc />,
    getLabel: () => "ONDC Report",
    isVisible: (role) => isSuperAdmin(role),
    order: 13,
  },
  {
    key: "10",
    path: "/employee-management",
    icon: <Employees />,
    getLabel: () => "Employee Management",
    isVisible: (role) => isShopAdmin(role) || isShopSupervisor(role),
    order: 10,
  },
  {
    key: "13",
    path: "/single-shop-catalog",
    dynamicPath: true,
    icon: <Product />,
    getLabel: () => "Catalog",
    isVisible: (role) =>
      isShopAdmin(role) ||
      isShopSupervisor(role) ||
      isShopStockManager(role),
    order: 8,
  },
  {
    key: "11",
    path: "/bills",
    icon: <Bills />,
    getLabel: () => "Bills",
    isVisible: (role, shopConfig) => {
      // Check if shop has invoice sync enabled (mandatory or optional)
      const invoiceLinkWithOrder =
        shopConfig?.pos?.invoiceSync?.invoiceLinkWithOrder;
      const hasInvoiceSync =
        invoiceLinkWithOrder === "mandatory" ||
        invoiceLinkWithOrder === "optional";
      // Check if user has appropriate role
      const hasValidRole =
        isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role);
      return hasInvoiceSync && hasValidRole;
    },
    order: 5,
  },
  {
    key: "15",
    path: "/customers",
    icon: <Customers />,
    getLabel: () => "Customers",
    isVisible: (role) =>
      isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role),
    order: 6,
  },
  {
    key: "14",
    path: "/single-shop-offers",
    dynamicPath: true,
    icon: <Offers />,
    getLabel: () => "Offers",
    isVisible: (role) => isShopAdmin(role) || isShopSupervisor(role),
    order: 9,
  },
  {
    key: "16",
    path: "/single-shop-subscription",
    dynamicPath: true,
    icon: <Subscription />,
    getLabel: () => "Subscription",
    // Admin roles reach the same page from the Shops table row actions, so
    // they intentionally get no sidebar item here.
    isVisible: (role) => canViewShopSubscription(role),
    order: 14,
  },
  {
    // Reuses the free key left behind by the removed Settings item.
    key: "8",
    path: "/subscription-plans",
    icon: <SubscriptionPlans />,
    getLabel: () => "Subscription Plans",
    // Platform-level plan catalogue, not shop-scoped — admins only.
    isVisible: (role) => canManageSubscriptions(role),
    order: 15,
  },
];

const getVisibleMenuItems = (role: string, shopConfig?: ShopConfig | null) =>
  MENU_ITEMS.filter((item) => item.isVisible(role, shopConfig));

// A single `order` field drives the menu sequence for every role. Each role
// only renders the items its `isVisible` predicate allows, but the relative
// order always follows this shared ordering.
const sortMenuItems = (items: MenuItemDefinition[]) =>
  [...items].sort((a, b) => a.order - b.order);

const SiderComponent: React.FC<SiderComponentProps> = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const { navigateWithCheck } = useVersionCheckContext();
  const location = useLocation();
  const role = getSession("role");
  const dispatch = useDispatch();

  // Use the hook to manage collapsed state with Redux persistence
  // This ensures the sidebar state persists across page navigation
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  // Mobile drawer: on phones the rail turns into a slide-in overlay toggled by
  // a hamburger button. Kept in local state (default closed) so it never
  // conflicts with the persisted desktop collapse preference.
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Reset the drawer when leaving mobile (e.g. rotation / resize to desktop).
  useEffect(() => {
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  // Lock background scroll while the drawer is open on mobile.
  useEffect(() => {
    if (isMobile && mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen]);

  // Get shop config from the per-shop cache (via ShopDataGate/useShopConfig).
  const shopConfig = useSelector(selectCurrentShopConfig) as
    | ShopConfig
    | null;

  // Get allShops from Redux for dynamic catalog path
  const allShops = useSelector(
    (state: RootState) => state.allShopManagement?.allShops || []
  );

  // The globally-selected shop (set by the shop switcher on the Orders page).
  // Dynamic sidebar links must target this shop, not the first one, so that
  // switching shops updates where Catalog/Offers navigate to.
  const selectedShopId = useSelector(selectShopId);

  // Resolve path for menu items - handles dynamic paths like catalog
  const getResolvedPath = (item: MenuItemDefinition): string => {
    // Dynamic items (Catalog, Offers) target the current shop, e.g.
    // "/single-shop-catalog/:id" or "/single-shop-offers/:id". Prefer the
    // globally-selected shop; fall back to the first shop only when nothing
    // is selected yet.
    if (item.dynamicPath) {
      const firstShopId = allShops.find((shop: any) => shop?.firestore?.id)
        ?.firestore?.id;
      const resolvedShopId = selectedShopId || firstShopId;
      if (resolvedShopId) {
        return `${item.path}/${resolvedShopId}`;
      }
      // Fallback if no shops are loaded yet
      return item.path;
    }
    return item.path;
  };

  // For shop users, catalog-related paths should highlight the Catalog menu item (key "13")
  // For admin users, they remain under Shops (key "4")
  const isShopRole = isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role) || isShopStockManager(role);
  const catalogKey = isShopRole ? "13" : "4";
  // Offers has its own sidebar item for shop management roles; other roles
  // still reach offers via the Shops table, so they keep highlighting "Shops".
  const offersKey = isShopAdmin(role) || isShopSupervisor(role) ? "14" : "4";
  // Subscription likewise has its own sidebar item for shop admin/supervisor;
  // admins arrive via the Shops table, so they keep highlighting "Shops".
  const subscriptionKey = canViewShopSubscription(role) ? "16" : "4";

  const pathToKey: Record<string, string> = {
    "/dashboard": "1",
    "/global-catalog-management": "2",
    "/create-product": "2",
    "/update-product": "2",
    "/catalog-automation-tool": "3",
    "/new-cat-upload": "3",
    "/migrate-product": "3",
    "/cat-up": "12",
    "/cat-up-product": "12",
    "/all-shops": "4",
    "/edit-shop": "4",
    "/single-shop-catalog": catalogKey,
    "/create-shop-product": catalogKey,
    "/update-shop-product": catalogKey,
    "/create-draft-product": catalogKey,
    "/update-draft-product": catalogKey,
    "/single-shop-offers": offersKey,
    "/create-new-offer": offersKey,
    "/update-offer": offersKey,
    "/add-from-catalog": catalogKey,
    "/single-shop-outgoing-payment": "4",
    "/single-shop-subscription": subscriptionKey,
    "/subscription-plans": "8",
    "/orders": "5",
    "/shop-kyc": "6",
    "/update-shop-kyc": "6",
    "/incoming-payments": "7",
    "/payment-details": "7",
    "/settings": "8",
    "/ondc-report": "9",
    "/employee-management": "10",
    "/bills": "11",
    "/customers": "15",
    "/shop-onboarding": "4",
    "/shop-config": "4",
  };

  const getKeyFromPath = (path: string): string | null => {
    const basePaths = Object.keys(pathToKey);
    const matchingBasePath = basePaths.find((basePath) => path.startsWith(basePath));
    return matchingBasePath ? pathToKey[matchingBasePath] : null;
  };

  const [selectedKey, setSelectedKey] = useState<string>(() => {
    // First try to get key from current URL path
    const keyFromPath = getKeyFromPath(window.location.pathname);
    if (keyFromPath) {
      return keyFromPath;
    }
    // Fallback to first visible menu item
    const defaultItem = sortMenuItems(
      getVisibleMenuItems(role, shopConfig)
    )[0];
    return defaultItem ? defaultItem.key : "1";
  });

  const visibleMenuItems = useMemo(
    () => sortMenuItems(getVisibleMenuItems(role, shopConfig)),
    [role, shopConfig]
  );

  // Keep the highlighted menu item in sync with the current route. Uses the
  // role-aware `pathToKey` map above (which resolves catalog paths to key "13"
  // for shop roles) so the correct item stays selected on navigation/refresh.
  useEffect(() => {
    const keyFromPath = getKeyFromPath(location.pathname);
    if (keyFromPath) {
      setSelectedKey(keyFromPath);
    }
  }, [location.pathname]);

  // On mobile the drawer is always expanded, so labels/logo should show
  // regardless of the persisted desktop collapse preference.
  const showLabels = isMobile || !collapsed;

  return (
    <>
      {isMobile && !mobileOpen && (
        <button
          type="button"
          className="mobile-nav-toggle"
          aria-label="Open navigation menu"
          onClick={() => setMobileOpen(true)}
        >
          <MenuOutlined />
        </button>
      )}
      {isMobile && mobileOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <Sider
        id="sider-main"
        className={
          isMobile
            ? `mobile-sider${mobileOpen ? " mobile-sider--open" : ""}`
            : undefined
        }
        width={isMobile ? "80vw" : "18vw"}
        trigger={
          isMobile ? null : collapsed ? <RightButtonArrow /> : <LeftArrow />
        }
        collapsible={!isMobile}
        collapsed={isMobile ? false : collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{
          background: colorBgContainer,
          position: "relative",
          padding: "1.2vw 0.8vw",
        }}
      >
        {isMobile && (
          <button
            type="button"
            className="mobile-nav-close"
            aria-label="Close navigation menu"
            onClick={() => setMobileOpen(false)}
          >
            <CloseOutlined />
          </button>
        )}
        <div className="logo-Icon">
          <img src={logo} alt="" className="logoDiv" />
          {showLabels && (
            <div>
              <img className="icon" src={logoicon} loading="lazy" alt="" />
            </div>
          )}
        </div>
      <Menu
        selectedKeys={[selectedKey]}
        mode="inline"
        style={{
          background: colorBgContainer,
          border: "none",
          height: "35vw",
          marginBottom: "15%",
        }}
        inlineCollapsed={isMobile ? false : collapsed}
      >
        {visibleMenuItems.map((item) => (
          <Menu.Item
            key={item.key}
            icon={item.icon}
            title={item.getLabel(role)}
            onClick={() => {
              const resolvedPath = getResolvedPath(item);
              // Dispatch shop details when navigating to a shop-scoped item
              // (Catalog, Offers) so the target page has its shop context,
              // matching AllShopsTable behavior. Use the globally-selected
              // shop (from the Orders shop switcher) and fall back to the
              // first shop only when nothing is selected yet — otherwise a
              // multi-shop user who switched shops would land on the first
              // shop's catalog/offers.
              if (item.dynamicPath) {
                const targetShop =
                  allShops.find(
                    (shop: any) => shop?.firestore?.id === selectedShopId
                  ) ?? allShops.find((shop: any) => shop?.firestore?.id);
                if (targetShop) {
                  dispatch(updateShopDetails(targetShop));
                }
              }
              navigateWithCheck(resolvedPath);
              setSelectedKey(item.key);
              setMobileOpen(false);
            }}
          >
            {showLabels && item.getLabel(role)}
          </Menu.Item>
        ))}
      </Menu>
      {!role?.includes("SHOP_") && (
        <Menu
          mode="inline"
          style={{
            background: colorBgContainer,
            border: "none",
            marginBottom: "100px",
          }}
          inlineCollapsed={isMobile ? false : collapsed}
        >
          <Menu.Item
            key="logout"
            icon={<Logout />}
            title={"Logout"}
            onClick={logout}
            style={{ color: "#CC5F5F" }}
          >
            {showLabels ? "Logout" : null}
          </Menu.Item>
        </Menu>
      )}
      </Sider>
    </>
  );
};

export default SiderComponent;
